import { Fragment, useEffect } from "react";
import { themeGet } from "styled-system";
import system from "system-components";

import PolicyRenderer from "../policy_styling/PolicyRenderer";

import createPolicyContent from "./PolicyParser";

import { StyledFontAwesomeIcon } from "pstat-anywhere/components/partials/icons";
import { withDocumentSettingsContext } from "pstat-anywhere/context_providers/DocumentSettingsContext";
import theme from "pstat-anywhere/themes/policystat/theme";
import { getAllHeadingsWithIds } from "pstat-anywhere/utils/domHelpers";
import Card from "pstat-design-system/Card";
import { H1 } from "pstat-design-system/text/headings";



const PolicyContentContainer = system({
  position: "relative"
}).extend`
  counter-reset: toc-autonumber-0 toc-autonumber-1 toc-autonumber-2 toc-autonumber-3 toc-autonumber-4 toc-autonumber-5;
`;

const PolicyContent = ({ html, documentSettings, tableOfContentsCallback }) => {
  let allPolicyHeaders = null;
  let activeElement = null;

  const updateTocHighlight = event => {
    if (allPolicyHeaders) {
      activeElement = getActiveElementAfterScroll(
        getAllHeadingsWithIds(allPolicyHeaders)
      );
      tableOfContentsCallback(activeElement);
    }
  };

  const getAllHeadingsFromDocumentBody = () => {
    allPolicyHeaders = document.querySelectorAll('[role="main"]')[0];
    if (allPolicyHeaders) {
      return getAllHeadingsWithIds(allPolicyHeaders);
    }
    return null;
  };

  useEffect(() => {
    let isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
    //This is a workaround for the IE11
    //i don't think they'll change the IE11's API anymore, but here's the link for the "solution"
    //https://stackoverflow.com/questions/21825157/internet-explorer-11-detection
    if (isIE11) {
      return;
    }
    if (!tableOfContentsCallback) {
      return;
    }

    const headers = getAllHeadingsFromDocumentBody();
    if (!headers) {
      return;
    }

    tableOfContentsCallback(
      getActiveElementAfterScroll(getAllHeadingsFromDocumentBody())
    );
    window.addEventListener("scroll", updateTocHighlight);
    return () => window.removeEventListener("scroll", updateTocHighlight);
  }, []);

  const { headerHtml, footerHtml, viewStyles } = documentSettings;
  return (
    <PolicyContentContainer>
      <Fragment>
        {headerHtml && (
          <PolicyRenderer otherStyles={viewStyles}>
            <div className="document">
              <div className="document_header">
                {createPolicyContent(headerHtml)}
              </div>
            </div>
          </PolicyRenderer>
        )}
        <PolicyRenderer role="main" otherStyles={viewStyles}>
          <div className="document">
            <div className="document_body">{createPolicyContent(html)}</div>
          </div>
        </PolicyRenderer>
        {footerHtml && (
          <PolicyRenderer otherStyles={viewStyles}>
            <div className="document">
              <div className="document_footer">
                {createPolicyContent(footerHtml)}
              </div>
            </div>
          </PolicyRenderer>
        )}
      </Fragment>
    </PolicyContentContainer>
  );
};

const PolicyTitle = H1.extend`
  text-align: center;
  margin: 0;
  word-break: break-word;
  word-wrap: break-word;
  font-weight: ${themeGet("fontWeights.bold")};
`;

const PolicyTitleContainer = system({
  style: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 0,
    marginBottom: `${theme.space[5]}px`,
    paddingBottom: `${theme.space[5]}px`,
    borderBottom: `1px solid ${theme.colors.nav[80]}`
  }
});

const PolicyContentCard = ({
  name,
  nameHtml,
  html,
  documentSettings,
  isRestricted,
  tableOfContentsCallback
}) => (
  <Card id="document_content">
    <PolicyTitleContainer>
      {nameHtml ? (
        <PolicyTitle dangerouslySetInnerHTML={{ __html: nameHtml }} />
      ) : (
        <PolicyTitle>{name}</PolicyTitle>
      )}
      {isRestricted && (
        <StyledFontAwesomeIcon
          icon={["fas", "lock"]}
          color="colors.nav.80"
          style={{ marginLeft: 5 }}
          data-testid="lock-icon-policy-title"
        />
      )}
    </PolicyTitleContainer>
    <PolicyContent
      html={html}
      documentSettings={documentSettings}
      tableOfContentsCallback={tableOfContentsCallback}
    />
  </Card>
);

export const getActiveElementAfterScroll = allPolicyHeaders => {
  let highestVisibleElementIndex = 0;
  for (let i = 0; i < allPolicyHeaders.length; i++) {
    const { top } = allPolicyHeaders[i].getBoundingClientRect();
    if (top <= 100) {
      highestVisibleElementIndex = i;
    }
  }
  const highestVisibleElement = allPolicyHeaders[highestVisibleElementIndex];
  return highestVisibleElement;
};

export default withDocumentSettingsContext(PolicyContentCard);
